import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { ArrowRight, Loader2, User, Building2, ShieldCheck } from "lucide-react";
import { CYRA_LOGO } from "@/lib/brand";
import { toast } from "sonner";

const PORTALS = {
  professional: {
    label: "Professional", icon: User, tagline: "For coaches, officials & sports professionals",
    demo: { email: "pro1@krida.demo", pw: "Krida@123" }, canRegister: true,
  },
  organisation: {
    label: "Organisation", icon: Building2, tagline: "For federations, clubs, academies & venues",
    demo: { email: "org1@krida.demo", pw: "Krida@123" }, canRegister: true,
  },
  admin: {
    label: "Krida Internal", icon: ShieldCheck, tagline: "Verification, operations & governance staff",
    demo: { email: "guhanmurthy@gmail.com", pw: "Krida@Admin2026" }, canRegister: false,
  },
};

export default function Login() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const initial = new URLSearchParams(window.location.search).get("portal");
  const [portal, setPortal] = useState(PORTALS[initial] ? initial : "professional");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const cfg = PORTALS[portal];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const user = await login(email.trim(), password);
      if (user.role !== portal) {
        await logout();
        setError(`This account is not a ${cfg.label} account. Use the ${PORTALS[user.role]?.label || user.role} portal.`);
        return;
      }
      toast.success(`Welcome to the ${cfg.label} portal`);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-krida-bg krida-grid-bg flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img src={CYRA_LOGO} alt="CYRA" className="h-14 w-auto" />
          <div className="h-8 w-px bg-slate-300" />
          <div className="font-serif italic text-krida-crimson text-3xl leading-none">Krida</div>
        </Link>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {Object.entries(PORTALS).map(([key, p]) => (
            <button key={key} data-testid={`portal-${key}`} onClick={() => { setPortal(key); setError(""); }}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs font-semibold transition-all ${portal === key ? "border-krida-crimson bg-krida-crimson/10 text-krida-crimson" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
              <p.icon className="w-4 h-4" /> {p.label}
            </button>
          ))}
        </div>

        <div className="krida-card p-8">
          <div className="overline text-krida-crimson mb-2 flex items-center gap-1.5"><cfg.icon className="w-3.5 h-3.5" /> {cfg.label} portal</div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-1">Sign in</h1>
          <p className="text-sm text-slate-500 mb-6">{cfg.tagline}</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline text-slate-500 block mb-1.5">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 focus:border-krida-crimson/60 outline-none transition-colors" placeholder="you@krida.demo" />
            </div>
            <div>
              <label className="overline text-slate-500 block mb-1.5">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 focus:border-krida-crimson/60 outline-none transition-colors" placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-krida-crimson bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="login-submit" disabled={busy} className="w-full py-3 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark text-krida-bg font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Enter {cfg.label} portal <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
          {cfg.canRegister ? (
            <p className="text-center text-slate-500 text-sm mt-5">
              New here? <Link to={`/register?portal=${portal}`} className="text-krida-crimson font-semibold">Create {cfg.label.toLowerCase()} account</Link>
            </p>
          ) : (
            <p className="text-center text-slate-600 text-xs mt-5">Krida internal accounts are provisioned by administrators.</p>
          )}
        </div>

        <button data-testid="quick-fill" onClick={() => { setEmail(cfg.demo.email); setPassword(cfg.demo.pw); }}
          className="w-full krida-card p-3 mt-4 text-xs text-slate-400 hover:text-slate-200 transition-colors">
          Use demo {cfg.label} login →
        </button>
      </div>
    </div>
  );
}
